import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import {
  X, File, Folder, FolderOpen, ChevronRight, ChevronDown,
  FilePlus, FolderPlus, Trash2, Pencil, Save, AlertCircle,
  Download, Search, Copy, Scissors, ClipboardPaste, Upload
} from 'lucide-react';
import { labs } from '../services/api';
import { getLanguage, buildFileTree } from '../utils/fileUtils';

function StarterCodeEditor({ labId, onClose }) {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [renameState, setRenameState] = useState(null);
  const [newItemState, setNewItemState] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [clipboard, setClipboard] = useState(null); // { path, type, operation: 'cut'|'copy' }
  const [dragState, setDragState] = useState(null); // { sourcePath, sourceType }
  const [dropTarget, setDropTarget] = useState(null); // path of folder being hovered
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set()); // multi-select
  const [externalDragOver, setExternalDragOver] = useState(false);
  const renameInputRef = useRef(null);
  const newItemInputRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const uploadInputRef = useRef(null);
  const sidebarRef = useRef(null);

  const { data: starterData } = useQuery({
    queryKey: ['starter-editor-files', labId],
    queryFn: () => labs.getStarterFiles(labId),
  });

  const files = starterData?.files?.filter(f => f.type === 'file') || [];
  const hasUnsavedChanges = selectedFile && editorContent !== originalContent;

  const { data: fileData, isLoading: fileLoading } = useQuery({
    queryKey: ['starter-editor-content', labId, selectedFile],
    queryFn: () => labs.getStarterFile(labId, selectedFile),
    enabled: !!selectedFile,
  });

  useEffect(() => {
    if (fileData && !fileData.binary) {
      setEditorContent(fileData.content || '');
      setOriginalContent(fileData.content || '');
    }
  }, [fileData]);

  const showStatus = useCallback((msg, isError = false) => {
    setStatusMessage({ text: msg, isError });
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    statusTimeoutRef.current = setTimeout(() => setStatusMessage(null), 3000);
  }, []);

  useEffect(() => {
    return () => { if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current); };
  }, []);

  const invalidateFiles = () => {
    queryClient.invalidateQueries(['starter-editor-files', labId]);
    queryClient.invalidateQueries(['starter-editor-content', labId]);
    queryClient.invalidateQueries(['lab-starter-code', labId]);
  };

  const getErrorMsg = (err) => err.response?.data?.error || err.message;

  // ── Mutations ──────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: ({ filePath, content }) => labs.updateStarterFile(labId, filePath, content),
    onSuccess: (_data, variables) => {
      setOriginalContent(variables.content);
      showStatus('File saved');
      queryClient.invalidateQueries(['starter-editor-files', labId]);
      queryClient.invalidateQueries(['lab-starter-code', labId]);
    },
    onError: (err) => showStatus(`Save failed: ${getErrorMsg(err)}`, true),
  });

  const createMutation = useMutation({
    mutationFn: ({ filePath, content }) => labs.createStarterFile(labId, filePath, content),
    onSuccess: (data) => {
      invalidateFiles();
      showStatus('File created');
      setSelectedFile(data.path);
    },
    onError: (err) => showStatus(`Create failed: ${getErrorMsg(err)}`, true),
  });

  const deleteMutation = useMutation({
    mutationFn: (filePath) => labs.deleteStarterFile(labId, filePath),
    onSuccess: (data, filePath) => {
      invalidateFiles();
      if (selectedFile === filePath || (selectedFile && selectedFile.startsWith(filePath + '/'))) {
        setSelectedFile(null);
        setEditorContent('');
        setOriginalContent('');
      }
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.delete(filePath);
        return next;
      });
      showStatus(`Deleted (${data.removedCount} item${data.removedCount > 1 ? 's' : ''})`);
    },
    onError: (err) => showStatus(`Delete failed: ${getErrorMsg(err)}`, true),
  });

  const renameMutation = useMutation({
    mutationFn: ({ oldPath, newPath }) => labs.renameStarterFile(labId, oldPath, newPath),
    onSuccess: (data) => {
      invalidateFiles();
      if (selectedFile === data.oldPath) {
        setSelectedFile(data.newPath);
      } else if (selectedFile && selectedFile.startsWith(data.oldPath + '/')) {
        setSelectedFile(data.newPath + selectedFile.substring(data.oldPath.length));
      }
      // Update clipboard if the moved item was in it
      if (clipboard && (clipboard.path === data.oldPath || clipboard.path.startsWith(data.oldPath + '/'))) {
        setClipboard(null);
      }
      // Distinguish rename (same parent) from move (different parent)
      const oldParent = data.oldPath.split('/').slice(0, -1).join('/');
      const newParent = data.newPath.split('/').slice(0, -1).join('/');
      showStatus(oldParent === newParent ? 'Renamed' : 'Moved');
    },
    onError: (err) => showStatus(`Move failed: ${getErrorMsg(err)}`, true),
  });

  const copyMutation = useMutation({
    mutationFn: ({ sourcePath, destinationPath }) => labs.copyStarterFile(labId, sourcePath, destinationPath),
    onSuccess: (data) => {
      invalidateFiles();
      showStatus(`Copied (${data.copiedCount} item${data.copiedCount > 1 ? 's' : ''})`);
    },
    onError: (err) => showStatus(`Copy failed: ${getErrorMsg(err)}`, true),
  });

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleSave = useCallback(() => {
    if (!selectedFile || !hasUnsavedChanges) return;
    saveMutation.mutate({ filePath: selectedFile, content: editorContent });
  }, [selectedFile, hasUnsavedChanges, editorContent, saveMutation]);

  // Ctrl+S to save
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  // Close context menu on click
  useEffect(() => {
    if (contextMenu) {
      const handler = () => setContextMenu(null);
      window.addEventListener('click', handler);
      return () => window.removeEventListener('click', handler);
    }
  }, [contextMenu]);

  // Focus rename/new item inputs
  useEffect(() => {
    if (renameState && renameInputRef.current) renameInputRef.current.focus();
  }, [renameState]);
  useEffect(() => {
    if (newItemState && newItemInputRef.current) newItemInputRef.current.focus();
  }, [newItemState]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Discard and close?')) return;
    }
    queryClient.invalidateQueries(['lab-starter-code', labId]);
    onClose();
  };

  const handleSelectFile = (filePath, e) => {
    // Multi-select with Ctrl/Cmd click
    if (e && (e.ctrlKey || e.metaKey)) {
      setSelectedItems(prev => {
        const next = new Set(prev);
        if (next.has(filePath)) next.delete(filePath);
        else next.add(filePath);
        return next;
      });
      return;
    }

    // Clear multi-select on normal click
    setSelectedItems(new Set());

    if (filePath === selectedFile) return;
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Discard?')) return;
    }
    setSelectedFile(filePath);
  };

  const toggleFolder = (path) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // ── Context Menu ───────────────────────────────────────────────────────

  const handleContextMenu = (e, itemPath, itemType) => {
    e.preventDefault();
    e.stopPropagation();
    const menuHeight = 260;
    const menuWidth = 190;
    const x = Math.min(e.clientX, window.innerWidth - menuWidth);
    const y = Math.min(e.clientY, window.innerHeight - menuHeight);
    setContextMenu({ x, y, path: itemPath, type: itemType });
  };

  const handleBackgroundContextMenu = (e) => {
    // Only if clicking on the sidebar background (not a tree item)
    if (e.target === e.currentTarget || e.target.closest('[data-sidebar-bg]')) {
      e.preventDefault();
      const menuHeight = 200;
      const menuWidth = 190;
      const x = Math.min(e.clientX, window.innerWidth - menuWidth);
      const y = Math.min(e.clientY, window.innerHeight - menuHeight);
      setContextMenu({ x, y, path: '', type: 'background' });
    }
  };

  // ── Rename ─────────────────────────────────────────────────────────────

  const startRename = (itemPath) => {
    const name = itemPath.split('/').pop();
    setRenameState({ path: itemPath, currentName: name });
    setContextMenu(null);
  };

  const commitRename = (newName) => {
    if (!renameState || !newName.trim()) {
      setRenameState(null);
      return;
    }
    const parts = renameState.path.split('/');
    parts[parts.length - 1] = newName.trim();
    const newPath = parts.join('/');
    if (newPath !== renameState.path) {
      renameMutation.mutate({ oldPath: renameState.path, newPath });
    }
    setRenameState(null);
  };

  // ── New Item ───────────────────────────────────────────────────────────

  const startNewItem = (parentPath, type) => {
    setNewItemState({ parentPath, type });
    setContextMenu(null);
    if (parentPath) {
      setExpandedFolders(prev => new Set([...prev, parentPath]));
    }
  };

  const commitNewItem = (name) => {
    if (!newItemState || !name.trim()) {
      setNewItemState(null);
      return;
    }
    const fullPath = newItemState.parentPath
      ? `${newItemState.parentPath}/${name.trim()}`
      : name.trim();
    if (newItemState.type === 'file') {
      createMutation.mutate({ filePath: fullPath, content: '' });
    } else {
      createMutation.mutate({ filePath: `${fullPath}/.gitkeep`, content: '' });
    }
    setNewItemState(null);
  };

  // ── Delete ─────────────────────────────────────────────────────────────

  const handleDelete = (itemPath, type) => {
    const label = type === 'folder' ? 'folder and all its contents' : 'file';
    if (!window.confirm(`Delete this ${label}?\n\n${itemPath}`)) return;
    deleteMutation.mutate(itemPath);
    setContextMenu(null);
  };

  const handleBulkDelete = () => {
    const count = selectedItems.size;
    if (count === 0) return;
    if (!window.confirm(`Delete ${count} selected item${count > 1 ? 's' : ''}?`)) return;
    setContextMenu(null);
    const items = [...selectedItems];
    const deleteNext = (i) => {
      if (i >= items.length) {
        setSelectedItems(new Set());
        return;
      }
      deleteMutation.mutate(items[i], { onSettled: () => deleteNext(i + 1) });
    };
    deleteNext(0);
  };

  // ── Duplicate ──────────────────────────────────────────────────────────

  const handleDuplicate = (itemPath, type) => {
    setContextMenu(null);
    const parts = itemPath.split('/');
    const name = parts.pop();

    let copyName;
    if (type === 'file') {
      const dotIdx = name.lastIndexOf('.');
      if (dotIdx > 0) {
        copyName = `${name.substring(0, dotIdx)} (copy)${name.substring(dotIdx)}`;
      } else {
        copyName = `${name} (copy)`;
      }
    } else {
      copyName = `${name} (copy)`;
    }

    const destPath = parts.length > 0 ? `${parts.join('/')}/${copyName}` : copyName;
    copyMutation.mutate({ sourcePath: itemPath, destinationPath: destPath });
  };

  // ── Cut / Copy / Paste ─────────────────────────────────────────────────

  const handleCut = (itemPath, type) => {
    setClipboard({ path: itemPath, type, operation: 'cut' });
    setContextMenu(null);
    showStatus('Cut to clipboard');
  };

  const handleCopy = (itemPath, type) => {
    setClipboard({ path: itemPath, type, operation: 'copy' });
    setContextMenu(null);
    showStatus('Copied to clipboard');
  };

  const handlePaste = (targetFolder) => {
    if (!clipboard) return;
    setContextMenu(null);

    const name = clipboard.path.split('/').pop();
    const destPath = targetFolder ? `${targetFolder}/${name}` : name;

    if (clipboard.operation === 'cut') {
      if (destPath !== clipboard.path) {
        renameMutation.mutate({ oldPath: clipboard.path, newPath: destPath });
      }
      setClipboard(null);
    } else {
      copyMutation.mutate({ sourcePath: clipboard.path, destinationPath: destPath });
    }
  };

  // Keyboard shortcuts for Cut/Copy/Paste on tree focus
  useEffect(() => {
    const handler = (e) => {
      // Only when NOT inside the Monaco editor or an input
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (document.activeElement?.classList?.contains('monaco-editor')) return;
      // Check if focus is within the editor area (monaco)
      if (document.activeElement?.closest('.monaco-editor')) return;

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
        if (e.key === 'x' && selectedFile) {
          e.preventDefault();
          handleCut(selectedFile, 'file');
        } else if (e.key === 'c' && selectedFile) {
          e.preventDefault();
          handleCopy(selectedFile, 'file');
        } else if (e.key === 'v' && clipboard) {
          e.preventDefault();
          // Paste into parent of selected file, or root
          const parent = selectedFile ? selectedFile.split('/').slice(0, -1).join('/') : '';
          handlePaste(parent);
        }
      }
      // Delete key
      if (e.key === 'Delete' && selectedFile) {
        e.preventDefault();
        handleDelete(selectedFile, 'file');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedFile, clipboard]);

  // ── Download ───────────────────────────────────────────────────────────

  const handleDownloadFile = async (filePath) => {
    setContextMenu(null);
    try {
      const data = await labs.getStarterFile(labId, filePath);
      if (data.binary) {
        showStatus('Cannot download binary files from editor', true);
        return;
      }
      const blob = new Blob([data.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showStatus(`Download failed: ${getErrorMsg(err)}`, true);
    }
  };

  const handleDownloadZip = async () => {
    try {
      showStatus('Preparing download...');
      const response = await labs.downloadStarterCode(labId);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `starter-code.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showStatus('Download complete');
    } catch (err) {
      showStatus(`Download failed: ${getErrorMsg(err)}`, true);
    }
  };

  // ── Upload File ────────────────────────────────────────────────────────

  const handleUploadFiles = useCallback((fileList, targetFolder = '') => {
    Array.from(fileList).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result;
        const filePath = targetFolder ? `${targetFolder}/${file.name}` : file.name;
        createMutation.mutate({ filePath, content });
      };
      reader.readAsText(file);
    });
  }, [createMutation]);

  // Drag from desktop onto sidebar
  const handleExternalDragOver = (e) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.stopPropagation();
      setExternalDragOver(true);
    }
  };

  const handleExternalDragLeave = (e) => {
    // Only reset if leaving the sidebar entirely
    if (sidebarRef.current && !sidebarRef.current.contains(e.relatedTarget)) {
      setExternalDragOver(false);
    }
  };

  const handleExternalDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExternalDragOver(false);
    // Ignore internal tree drags — those are handled by handleDrop
    if (dragState) return;
    if (e.dataTransfer.files.length > 0) {
      handleUploadFiles(e.dataTransfer.files);
    }
  };

  // ── Drag-and-Drop Move ─────────────────────────────────────────────────

  const handleDragStart = (e, itemPath, itemType) => {
    // Don't interfere with external file drag
    if (e.dataTransfer.types.includes('Files')) return;
    setDragState({ sourcePath: itemPath, sourceType: itemType });
    e.dataTransfer.setData('text/plain', itemPath);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, folderPath) => {
    e.preventDefault();
    e.stopPropagation();
    // Don't allow drop on external files (handled separately)
    if (e.dataTransfer.types.includes('Files') && !dragState) return;
    if (!dragState) return;

    // Guard: can't drop into itself or a child
    if (folderPath === dragState.sourcePath) return;
    if (folderPath.startsWith(dragState.sourcePath + '/')) return;

    e.dataTransfer.dropEffect = 'move';
    setDropTarget(folderPath);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setDropTarget(null);
  };

  const handleDrop = (e, targetFolder) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);

    if (!dragState) {
      // External file drop on a folder
      if (e.dataTransfer.files.length > 0) {
        handleUploadFiles(e.dataTransfer.files, targetFolder);
      }
      return;
    }

    const { sourcePath, sourceType } = dragState;
    setDragState(null);

    // Guard: don't move to same parent
    const sourceParent = sourcePath.split('/').slice(0, -1).join('/');
    if (sourceParent === targetFolder) return;

    // Guard: can't drop into itself
    if (targetFolder === sourcePath || targetFolder.startsWith(sourcePath + '/')) return;

    const name = sourcePath.split('/').pop();
    const newPath = targetFolder ? `${targetFolder}/${name}` : name;

    if (newPath !== sourcePath) {
      renameMutation.mutate({ oldPath: sourcePath, newPath });
    }
  };

  const handleDragEnd = () => {
    setDragState(null);
    setDropTarget(null);
  };

  // ── Search ─────────────────────────────────────────────────────────────

  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim()) return files;
    const term = searchTerm.toLowerCase();
    return files.filter(f => f.path.toLowerCase().includes(term));
  }, [files, searchTerm]);

  // Auto-expand all folders when searching
  const searchExpandedFolders = useMemo(() => {
    if (!searchTerm.trim()) return null;
    const folders = new Set();
    filteredFiles.forEach(f => {
      const parts = f.path.split('/');
      for (let i = 1; i < parts.length; i++) {
        folders.add(parts.slice(0, i).join('/'));
      }
    });
    return folders;
  }, [filteredFiles, searchTerm]);

  const effectiveExpandedFolders = searchExpandedFolders || expandedFolders;

  // ── Render Helpers ─────────────────────────────────────────────────────

  const renderRenameInput = () => (
    <input
      ref={renameInputRef}
      defaultValue={renameState.currentName}
      style={inputStyle}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commitRename(e.target.value);
        if (e.key === 'Escape') setRenameState(null);
      }}
      onBlur={(e) => commitRename(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const renderNewItemInput = (depth) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', paddingLeft: depth * 16 + 8 }}>
      <span style={{ width: 14 }} />
      {newItemState.type === 'folder'
        ? <Folder size={14} style={{ color: 'var(--accent-warning)' }} />
        : <File size={14} style={{ color: 'var(--text-muted)' }} />
      }
      <input
        ref={newItemInputRef}
        placeholder={newItemState.type === 'folder' ? 'folder name' : 'filename.ext'}
        style={inputStyle}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitNewItem(e.target.value);
          if (e.key === 'Escape') setNewItemState(null);
        }}
        onBlur={(e) => {
          if (e.target.value.trim()) commitNewItem(e.target.value);
          else setNewItemState(null);
        }}
      />
    </div>
  );

  const renderTree = (node, parentPath = '', depth = 0) => {
    const entries = Object.entries(node).sort(([, a], [, b]) => {
      const aIsFolder = !a._isFile;
      const bIsFolder = !b._isFile;
      if (aIsFolder && !bIsFolder) return -1;
      if (!aIsFolder && bIsFolder) return 1;
      return 0;
    });

    return entries.map(([name, item]) => {
      const fullPath = parentPath ? `${parentPath}/${name}` : name;
      const isFolder = !item._isFile;
      const isExpanded = effectiveExpandedFolders.has(fullPath);
      const isSelected = item._isFile && selectedFile === item.path;
      const isMultiSelected = selectedItems.has(fullPath);
      const isRenaming = renameState?.path === fullPath;
      const isDragSource = dragState?.sourcePath === fullPath;
      const isDropTarget = dropTarget === fullPath;
      const isCutItem = clipboard?.operation === 'cut' && (clipboard.path === fullPath || fullPath.startsWith(clipboard.path + '/'));

      return (
        <div key={fullPath}>
          <div
            draggable={!isRenaming}
            onDragStart={(e) => handleDragStart(e, fullPath, isFolder ? 'folder' : 'file')}
            onDragEnd={handleDragEnd}
            onDragOver={isFolder ? (e) => handleDragOver(e, fullPath) : undefined}
            onDragLeave={isFolder ? handleDragLeave : undefined}
            onDrop={isFolder ? (e) => handleDrop(e, fullPath) : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 8px',
              paddingLeft: depth * 16 + 8,
              cursor: 'pointer',
              fontSize: 13,
              background: isDropTarget
                ? 'var(--accent-purple-transparent, rgba(139,92,246,0.2))'
                : isSelected || isMultiSelected
                  ? 'var(--accent-purple-transparent, rgba(139,92,246,0.15))'
                  : 'transparent',
              borderLeft: isDropTarget
                ? '2px solid var(--accent-purple, #8b5cf6)'
                : isSelected
                  ? '2px solid var(--accent-purple, #8b5cf6)'
                  : '2px solid transparent',
              opacity: isDragSource || isCutItem ? 0.4 : 1,
              transition: 'background 0.1s, border-left 0.1s, opacity 0.15s',
            }}
            onClick={(e) => isFolder ? toggleFolder(fullPath) : handleSelectFile(item.path, e)}
            onContextMenu={(e) => handleContextMenu(e, fullPath, isFolder ? 'folder' : 'file')}
          >
            {isFolder ? (
              <>
                <span style={{ width: 14, display: 'flex', alignItems: 'center' }}>
                  {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                {isExpanded
                  ? <FolderOpen size={14} style={{ color: 'var(--accent-warning)' }} />
                  : <Folder size={14} style={{ color: 'var(--accent-warning)' }} />
                }
                {isRenaming ? renderRenameInput() : (
                  <span>{name}</span>
                )}
              </>
            ) : (
              <>
                <span style={{ width: 14 }} />
                <File size={14} style={{ color: 'var(--text-muted)' }} />
                {isRenaming ? renderRenameInput() : (
                  <span style={{ flex: 1 }}>
                    {name}
                    {selectedFile === item.path && hasUnsavedChanges && (
                      <span style={{ color: 'var(--accent-warning)', marginLeft: 4 }} title="Unsaved changes">*</span>
                    )}
                  </span>
                )}
              </>
            )}
          </div>
          {isFolder && isExpanded && item._children && (
            <>
              {renderTree(item._children, fullPath, depth + 1)}
              {newItemState?.parentPath === fullPath && renderNewItemInput(depth + 1)}
            </>
          )}
        </div>
      );
    });
  };

  const fileTree = useMemo(() => buildFileTree(filteredFiles), [filteredFiles]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="modal-overlay">
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <span style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
            Starter Code Editor
            {hasUnsavedChanges && (
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--accent-warning-transparent, rgba(234,179,8,0.15))', color: 'var(--accent-warning)' }}>
                Unsaved
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {statusMessage && (
              <span style={{
                fontSize: 12,
                color: statusMessage.isError ? 'var(--accent-danger)' : 'var(--accent-success)',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                {statusMessage.isError ? <AlertCircle size={12} /> : null}
                {statusMessage.text}
              </span>
            )}
            {clipboard && (
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>
                {clipboard.operation === 'cut' ? 'Cut' : 'Copied'}: {clipboard.path.split('/').pop()}
              </span>
            )}
            {selectedItems.size > 0 && (
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--accent-purple-transparent, rgba(139,92,246,0.15))', color: 'var(--accent-purple, #8b5cf6)' }}>
                {selectedItems.size} selected
              </span>
            )}
            {files.length > 0 && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleDownloadZip}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                title="Download all as ZIP"
              >
                <Download size={12} />
                ZIP
              </button>
            )}
            {selectedFile && hasUnsavedChanges && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={saveMutation.isPending}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {saveMutation.isPending ? <div className="spinner" style={{ width: 12, height: 12 }} /> : <Save size={12} />}
                Save
              </button>
            )}
            <button
              onClick={handleClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}
              title="Close editor"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Sidebar */}
          <div
            ref={sidebarRef}
            style={{
              ...sidebarStyle,
              outline: externalDragOver ? '2px dashed var(--accent-purple, #8b5cf6)' : 'none',
              outlineOffset: '-2px',
            }}
            onDragOver={handleExternalDragOver}
            onDragLeave={handleExternalDragLeave}
            onDrop={handleExternalDrop}
          >
            {/* Sidebar header */}
            <div style={sidebarHeaderStyle}>
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' }}>
                Files
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => uploadInputRef.current?.click()}
                  style={iconBtnStyle}
                  title="Upload file"
                >
                  <Upload size={14} />
                </button>
                <button
                  onClick={() => startNewItem('', 'file')}
                  style={iconBtnStyle}
                  title="New file"
                >
                  <FilePlus size={14} />
                </button>
                <button
                  onClick={() => startNewItem('', 'folder')}
                  style={iconBtnStyle}
                  title="New folder"
                >
                  <FolderPlus size={14} />
                </button>
              </div>
            </div>

            {/* Search */}
            {files.length > 3 && (
              <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-primary)' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Search size={12} style={{ position: 'absolute', left: 8, color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      ...inputStyle,
                      paddingLeft: 26,
                      paddingRight: searchTerm ? 26 : 6,
                      border: '1px solid var(--border-primary)',
                      width: '100%',
                    }}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      style={{ position: 'absolute', right: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex' }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, paddingLeft: 4 }}>
                    {filteredFiles.length} match{filteredFiles.length !== 1 ? 'es' : ''}
                  </div>
                )}
              </div>
            )}

            {/* File tree */}
            <div
              style={{ flex: 1, overflow: 'auto', fontFamily: 'var(--font-mono)', padding: '4px 0' }}
              data-sidebar-bg="true"
              onContextMenu={handleBackgroundContextMenu}
              onDragOver={(e) => { e.preventDefault(); if (dragState) { setDropTarget(''); e.dataTransfer.dropEffect = 'move'; } }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) => handleDrop(e, '')}
            >
              {files.length === 0 && !searchTerm ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                  {externalDragOver ? (
                    <p>Drop files here to upload</p>
                  ) : (
                    <>
                      <p>No files yet.</p>
                      <p style={{ marginTop: 4 }}>Create a new file or drag files here.</p>
                    </>
                  )}
                </div>
              ) : filteredFiles.length === 0 && searchTerm ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                  No files matching "{searchTerm}"
                </div>
              ) : (
                renderTree(fileTree)
              )}
              {newItemState?.parentPath === '' && renderNewItemInput(0)}

              {/* Root drop zone indicator */}
              {dragState && dropTarget === '' && (
                <div style={{
                  margin: '4px 8px',
                  padding: '6px 12px',
                  borderRadius: 4,
                  border: '1px dashed var(--accent-purple, #8b5cf6)',
                  color: 'var(--accent-purple, #8b5cf6)',
                  fontSize: 11,
                  textAlign: 'center',
                }}>
                  Drop here to move to root
                </div>
              )}
            </div>
          </div>

          {/* Editor Area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedFile ? (
              <>
                <div style={tabBarStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <File size={12} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{selectedFile}</span>
                    {hasUnsavedChanges && <span style={{ color: 'var(--accent-warning)' }}>*</span>}
                  </div>
                </div>
                {fileLoading ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="spinner" />
                  </div>
                ) : fileData?.binary ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                    <AlertCircle size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
                    <p>Binary file - cannot edit</p>
                    <p style={{ fontSize: 12 }}>({(fileData.size / 1024).toFixed(1)} KB)</p>
                  </div>
                ) : (
                  <div style={{ flex: 1 }}>
                    <Editor
                      height="100%"
                      language={getLanguage(selectedFile)}
                      value={editorContent}
                      onChange={(value) => setEditorContent(value || '')}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        wordWrap: 'off',
                        automaticLayout: true,
                        tabSize: 2,
                        padding: { top: 8 },
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                <File size={48} style={{ marginBottom: 16, opacity: 0.2 }} />
                <p style={{ fontSize: 14 }}>Select a file to edit</p>
                <p style={{ fontSize: 12, marginTop: 4 }}>Right-click items for more options</p>
                <p style={{ fontSize: 11, marginTop: 8, color: 'var(--text-muted)', opacity: 0.6 }}>
                  Tip: Drag files to move them &bull; Ctrl+S to save &bull; Ctrl+X/C/V to cut/copy/paste
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Hidden upload input */}
        <input
          ref={uploadInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files.length > 0) {
              handleUploadFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />

        {/* Context Menu */}
        {contextMenu && (
          <div style={{ ...contextMenuStyle, top: contextMenu.y, left: contextMenu.x }}>
            {/* Multi-select actions */}
            {selectedItems.size > 1 ? (
              <>
                <button style={{ ...menuItemStyle, color: 'var(--accent-danger)' }} onClick={handleBulkDelete}>
                  <Trash2 size={13} /> Delete {selectedItems.size} Items
                </button>
              </>
            ) : (
              <>
                {/* Folder-specific: new file/folder */}
                {(contextMenu.type === 'folder' || contextMenu.type === 'background') && (
                  <>
                    <button style={menuItemStyle} onClick={() => startNewItem(contextMenu.path, 'file')}>
                      <FilePlus size={13} /> New File
                    </button>
                    <button style={menuItemStyle} onClick={() => startNewItem(contextMenu.path, 'folder')}>
                      <FolderPlus size={13} /> New Folder
                    </button>
                    {clipboard && (
                      <button style={menuItemStyle} onClick={() => handlePaste(contextMenu.path)}>
                        <ClipboardPaste size={13} /> Paste
                      </button>
                    )}
                    <div style={menuDividerStyle} />
                  </>
                )}

                {/* File-specific: download + paste into parent */}
                {contextMenu.type === 'file' && (
                  <>
                    <button style={menuItemStyle} onClick={() => handleDownloadFile(contextMenu.path)}>
                      <Download size={13} /> Download
                    </button>
                    {clipboard && (
                      <button style={menuItemStyle} onClick={() => handlePaste(contextMenu.path.split('/').slice(0, -1).join('/'))}>
                        <ClipboardPaste size={13} /> Paste Here
                      </button>
                    )}
                  </>
                )}

                {/* Common: cut, copy, duplicate, rename, delete */}
                {contextMenu.type !== 'background' && (
                  <>
                    <button style={menuItemStyle} onClick={() => handleCut(contextMenu.path, contextMenu.type)}>
                      <Scissors size={13} /> Cut
                    </button>
                    <button style={menuItemStyle} onClick={() => handleCopy(contextMenu.path, contextMenu.type)}>
                      <Copy size={13} /> Copy
                    </button>
                    <button style={menuItemStyle} onClick={() => handleDuplicate(contextMenu.path, contextMenu.type)}>
                      <Copy size={13} /> Duplicate
                    </button>
                    <div style={menuDividerStyle} />
                    <button style={menuItemStyle} onClick={() => startRename(contextMenu.path)}>
                      <Pencil size={13} /> Rename
                    </button>
                    <button style={{ ...menuItemStyle, color: 'var(--accent-danger)' }} onClick={() => handleDelete(contextMenu.path, contextMenu.type)}>
                      <Trash2 size={13} /> Delete
                    </button>
                  </>
                )}

                {/* Background: paste and download zip */}
                {contextMenu.type === 'background' && (
                  <>
                    {files.length > 0 && (
                      <button style={menuItemStyle} onClick={handleDownloadZip}>
                        <Download size={13} /> Download as ZIP
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const containerStyle = {
  width: '95vw',
  height: '90vh',
  background: 'var(--bg-primary)',
  borderRadius: 12,
  border: '1px solid var(--border-primary)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 16px',
  borderBottom: '1px solid var(--border-primary)',
  background: 'var(--bg-secondary)',
};

const sidebarStyle = {
  width: 260,
  minWidth: 200,
  borderRight: '1px solid var(--border-primary)',
  background: 'var(--bg-secondary)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const sidebarHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-primary)',
};

const tabBarStyle = {
  padding: '6px 12px',
  borderBottom: '1px solid var(--border-primary)',
  background: 'var(--bg-secondary)',
};

const iconBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  padding: 4,
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
};

const inputStyle = {
  background: 'var(--bg-tertiary)',
  border: '1px solid var(--accent-purple, #8b5cf6)',
  borderRadius: 3,
  color: 'var(--text-primary)',
  fontSize: 13,
  padding: '2px 6px',
  outline: 'none',
  flex: 1,
  fontFamily: 'var(--font-mono)',
};

const contextMenuStyle = {
  position: 'fixed',
  zIndex: 1100,
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-primary)',
  borderRadius: 8,
  padding: 4,
  minWidth: 170,
  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
};

const menuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '6px 12px',
  background: 'none',
  border: 'none',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontSize: 13,
  borderRadius: 4,
  textAlign: 'left',
};

const menuDividerStyle = {
  height: 1,
  background: 'var(--border-primary)',
  margin: '4px 0',
};

export default StarterCodeEditor;
